import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import SvgIcon from '@home/components/SvgIcon';
import EllipsisHover from '@home/components/EllipsisHover';

import {
  getShortcutDisplayCover,
  getShortcutDisplayName,
  getShortcutPlatformTypes,
  getUrlCollectionPrimaryEntityType,
} from '@home/utils/urlCollectionData';
import { getEntityTypeIcon } from '@home/pages/home/Explore/_config/common';
import { getUrlCollectionSystemDisplayInfo } from '../../utils/systemDisplay';
import { EntityType } from '@home/enums/urlCollection';

import type { IUrlCollectionItem } from '@home/types/urlCollection';

import './index.scss';

import ICON_COMBINE from '@home/assets/svg/urlCollection/combine.svg';

interface IShortcutCardProps {
  item: IUrlCollectionItem;
  onClick?: (item: IUrlCollectionItem) => void;
}

const ShortcutCard: React.FC<IShortcutCardProps> = (props) => {
  const { item, onClick } = props;
  const { formatMessage } = useIntl();
  const title = getShortcutDisplayName(item);
  const platformTypes = getShortcutPlatformTypes(item);
  const entityType = getUrlCollectionPrimaryEntityType(item.entity_type);
  const { systemIcon, systemName } = getUrlCollectionSystemDisplayInfo({
    system: item.system,
    formatMessage,
  });
  const displayImage = getShortcutDisplayCover(item, systemIcon);
  const isClickable = typeof onClick === 'function';
  const hasEntityType = !!entityType;
  const showEntityTypeIcon = hasEntityType && !!platformTypes?.length;
  const showSystemName = !!systemName;
  const showCoverAndMask = !!displayImage;

  const handleItemClick = (): void => {
    onClick?.(item);
  };

  return (
    <div
      className={classnames('dbh-shortcut-card', 'dbh-url-collection-card-base', {
        'dbh-url-collection-card-base--clickable': isClickable,
      })}
      onClick={handleItemClick}
    >
      {showCoverAndMask && (
        <div
          style={{ backgroundImage: `url('${displayImage}')` }}
          className={classnames('dbh-shortcut-card__cover dbh-url-collection-card-base__cover', {
            'dbh-url-collection-card-base__cover--has-entity-type': hasEntityType,
          })}
        />
      )}
      {showCoverAndMask && <div className="dbh-shortcut-card__mask dbh-url-collection-card-base__mask" />}
      <div className="dbh-shortcut-card__content dbh-url-collection-card-base__content">
        <EllipsisHover className="dbh-shortcut-card__title" placement="top" text={title} />
        <div className="dbh-shortcut-card__footer dbh-url-collection-card-base__footer">
          {showEntityTypeIcon && (
            <>
              {platformTypes?.map((platformType) => {
                const platformIcon = getEntityTypeIcon(platformType);

                if (!platformIcon) {
                  return null;
                }

                if (platformType === EntityType.Combine) {
                  return (
                    <img className="dbh-shortcut-card__platform-image" key={platformType} src={ICON_COMBINE} alt="" />
                  );
                }

                return <SvgIcon className="dbh-shortcut-card__platform-icon" key={platformType} type={platformIcon} />;
              })}
            </>
          )}
          {showEntityTypeIcon && showSystemName && (
            <span className="dbh-shortcut-card__divider dbh-url-collection-card-base__divider" />
          )}
          {showSystemName && (
            <span className="dbh-shortcut-card__system-name dbh-url-collection-card-base__system-name">
              {systemName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShortcutCard;
